import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card, Badge, Button, Alert } from '../components/design-system';
import { Input, TextArea, Select } from '../components/design-system';
import {
  Truck, Car, Bike, Package, Star, MapPin, ToggleLeft, ToggleRight,
  Edit, Save, Users, Box, Footprints,
} from 'lucide-react';
import courierApi from '../services/courierApi';
import { useGeolocation } from '../hooks/useGeolocation';

export const ProviderPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { position } = useGeolocation();
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState({
    displayName: '',
    description: '',
    phone: '',
    vehicleType: 'car',
    capacityKg: 0,
    capacityPassengers: 0,
    serviceTypes: ['parcel'],
    serviceRadiusKm: 50,
    baseLatitude: null,
    baseLongitude: null,
  });

  useEffect(() => {
    loadProfile();
  }, []);

  useEffect(() => {
    if (position && !form.baseLatitude) {
      setForm(f => ({
        ...f,
        baseLatitude: position.latitude,
        baseLongitude: position.longitude,
      }));
    }
  }, [position]);

  const loadProfile = async () => {
    setLoading(true);
    try {
      const res = await courierApi.getMyProviderProfile();
      setProfile(res.profile);
      setForm({
        displayName: res.profile.displayName || '',
        description: res.profile.description || '',
        phone: res.profile.phone || '',
        vehicleType: res.profile.vehicleType || 'car',
        capacityKg: res.profile.capacityKg || 0,
        capacityPassengers: res.profile.capacityPassengers || 0,
        serviceTypes: res.profile.serviceTypes || ['parcel'],
        serviceRadiusKm: res.profile.serviceRadiusKm || 50,
        baseLatitude: res.profile.baseLatitude,
        baseLongitude: res.profile.baseLongitude,
      });
    } catch {
      setProfile(null);
      setEditing(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');

    try {
      if (profile) {
        await courierApi.updateProviderProfile(form);
        setSuccess(t('common.success'));
      } else {
        await courierApi.createProviderProfile(form);
        setSuccess(t('common.success'));
      }
      setEditing(false);
      await loadProfile();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async () => {
    if (!profile) return;
    setSaving(true);
    try {
      await courierApi.updateProviderProfile({ isActive: !profile.isActive });
      await loadProfile();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  const toggleServiceType = (type) => {
    setForm(f => ({
      ...f,
      serviceTypes: f.serviceTypes.includes(type)
        ? f.serviceTypes.filter(t => t !== type)
        : [...f.serviceTypes, type],
    }));
  };

  const vehicleIcons = { car: Car, van: Truck, truck: Truck, bike: Bike, walk: Footprints };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin h-8 w-8 border-3 border-primary-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  return (
    <div className="p-4 pb-2">
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-lg font-bold text-gray-900">
          {profile ? t('provider.myProfile') : t('provider.becomeProvider')}
        </h1>
        {profile && !editing && (
          <Button variant="ghost" size="sm" onClick={() => setEditing(true)}>
            <Edit className="h-4 w-4" />
            {t('common.edit')}
          </Button>
        )}
      </div>

      {error && (
        <Alert variant="error" className="mb-4" dismissible onDismiss={() => setError('')}>
          {error}
        </Alert>
      )}
      {success && (
        <Alert variant="success" className="mb-4" dismissible onDismiss={() => setSuccess('')}>
          {success}
        </Alert>
      )}

      {/* Profile view */}
      {profile && !editing && (
        <div className="space-y-3">
          <Card padding="sm">
            <div className="flex items-center gap-3 mb-3">
              <div className="h-14 w-14 rounded-full bg-primary-100 flex items-center justify-center">
                <Truck className="h-7 w-7 text-primary-600" />
              </div>
              <div className="flex-1">
                <h2 className="text-base font-bold text-gray-900">{profile.displayName}</h2>
                <p className="text-xs text-gray-500">{t(`provider.vehicleTypes.${profile.vehicleType}`)}</p>
              </div>
              <button onClick={toggleActive} disabled={saving}>
                {profile.isActive ? (
                  <ToggleRight className="h-8 w-8 text-primary-500" />
                ) : (
                  <ToggleLeft className="h-8 w-8 text-gray-400" />
                )}
              </button>
            </div>

            {profile.description && (
              <p className="text-sm text-gray-600 mb-3">{profile.description}</p>
            )}

            <div className="grid grid-cols-2 gap-3">
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{profile.ratingAvg || '—'}</p>
                <p className="text-xs text-gray-500 flex items-center justify-center gap-1">
                  <Star className="h-3 w-3 text-yellow-500" />
                  {t('provider.rating')} ({profile.ratingCount})
                </p>
              </div>
              <div className="bg-gray-50 rounded-lg p-2 text-center">
                <p className="text-lg font-bold text-gray-900">{profile.serviceRadiusKm} km</p>
                <p className="text-xs text-gray-500">{t('provider.serviceRadius')}</p>
              </div>
            </div>
          </Card>

          <Card padding="sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('provider.serviceTypes')}</h3>
            <div className="flex flex-wrap gap-2">
              {profile.serviceTypes.map(type => (
                <Badge key={type} variant="primary" size="sm">
                  {t(`request.types.${type}`)}
                </Badge>
              ))}
            </div>
          </Card>

          <Card padding="sm">
            <h3 className="text-sm font-semibold text-gray-900 mb-2">{t('provider.capacity')}</h3>
            <div className="grid grid-cols-2 gap-2 text-sm">
              <div className="flex items-center gap-2 text-gray-600">
                <Package className="h-4 w-4" />
                <span>{profile.capacityKg} kg</span>
              </div>
              <div className="flex items-center gap-2 text-gray-600">
                <Users className="h-4 w-4" />
                <span>{profile.capacityPassengers} {t('request.types.persons').toLowerCase()}</span>
              </div>
            </div>
          </Card>

          <Button fullWidth variant="outline" onClick={() => navigate('/marketplace')}>
            {t('provider.marketplace')}
          </Button>
        </div>
      )}

      {/* Edit/Create form */}
      {(editing || !profile) && (
        <form onSubmit={handleSave} className="space-y-4">
          <Input
            label={t('provider.displayName')}
            placeholder={t('provider.displayNamePlaceholder')}
            value={form.displayName}
            onChange={(e) => setForm({ ...form, displayName: e.target.value })}
            required
            fullWidth
          />

          <TextArea
            label={t('provider.description')}
            placeholder={t('provider.descriptionPlaceholder')}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            rows={3}
            fullWidth
          />

          <Input
            label={t('provider.phone')}
            type="tel"
            value={form.phone}
            onChange={(e) => setForm({ ...form, phone: e.target.value })}
            fullWidth
          />

          <Select
            label={t('provider.vehicleType')}
            value={form.vehicleType}
            onChange={(e) => setForm({ ...form, vehicleType: e.target.value })}
            options={[
              { value: 'car', label: t('provider.vehicleTypes.car') },
              { value: 'van', label: t('provider.vehicleTypes.van') },
              { value: 'truck', label: t('provider.vehicleTypes.truck') },
              { value: 'bike', label: t('provider.vehicleTypes.bike') },
              { value: 'walk', label: t('provider.vehicleTypes.walk') },
            ]}
            fullWidth
          />

          <div className="grid grid-cols-2 gap-3">
            <Input
              label={t('provider.capacityKg')}
              type="number"
              min="0"
              value={form.capacityKg}
              onChange={(e) => setForm({ ...form, capacityKg: parseFloat(e.target.value) || 0 })}
              fullWidth
            />
            <Input
              label={t('provider.capacityPassengers')}
              type="number"
              min="0"
              value={form.capacityPassengers}
              onChange={(e) => setForm({ ...form, capacityPassengers: parseInt(e.target.value) || 0 })}
              fullWidth
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('provider.serviceTypes')}
            </label>
            <div className="flex flex-wrap gap-2">
              {['persons', 'parcel', 'large_objects'].map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => toggleServiceType(type)}
                  className={`px-3 py-1.5 rounded-full text-xs font-medium transition-colors ${
                    form.serviceTypes.includes(type)
                      ? 'bg-primary-500 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {t(`request.types.${type}`)}
                </button>
              ))}
            </div>
          </div>

          <Input
            label={t('provider.serviceRadius')}
            type="number"
            min="1"
            max="500"
            value={form.serviceRadiusKm}
            onChange={(e) => setForm({ ...form, serviceRadiusKm: parseFloat(e.target.value) || 50 })}
            fullWidth
          />

          <div className="flex gap-2">
            {profile && (
              <Button variant="secondary" fullWidth onClick={() => setEditing(false)}>
                {t('common.cancel')}
              </Button>
            )}
            <Button type="submit" fullWidth loading={saving}>
              <Save className="h-4 w-4" />
              {t('provider.saveProfile')}
            </Button>
          </div>
        </form>
      )}
    </div>
  );
};
